//  Home page Charts tabs Slice
import { createSlice, PayloadAction } from '@reduxjs/toolkit';

/** Shape produced by HomeFilterDropdown for multi-select filters. */
export interface FilterSelectionPayload {
  select_all: boolean;
  values: Array<string | number>;
}

export interface FilterQuery {
  // ── Date range ───────────────────────────────────────────────────────────
  start_date: string | null;
  end_date: string | null;
  period: string | null;
  date_type: string;

  // ── Segmentation ─────────────────────────────────────────────────────────
  segment: string | null;

  // ── Chart-specific tab state ─────────────────────────────────────────────
  data_type: string;
  otp_data_type: string;
  sa_data_type: string;
  chart_type: string;
  transit_time_filter: string;
  cycle_time_filter: string;
  metric_type: string;
  invoice_disputes_filter: string;
  cost_filter: string;
  freight_markup_filter: string;

  // ── Entity filters (previously untyped extras) ───────────────────────────
  teams: FilterSelectionPayload | null;
  transporters: FilterSelectionPayload | null;
  service_levels: FilterSelectionPayload | null;
  origins: FilterSelectionPayload | null;
  destinations: FilterSelectionPayload | null;
  products: FilterSelectionPayload | null;

  // ── Alert filters ────────────────────────────────────────────────────────
  alert_states: string[] | null;
  alert_types: string[] | null;

  // ── Reports ──────────────────────────────────────────────────────────────
  compare_to: string | null;
}

export type TabChangeType =
  'tab' | 'filter' | 'alert_filter' | 'initial' | null;

export interface TabState {
  tabs: Record<string, string>;
  filterQueryRedux: FilterQuery | null;
  changeType: TabChangeType;
  changedChartType: string | null;
}

// default state
export const defaultTabs: Record<string, string> = {
  transit_time: 'median',
  cycle_time: 'median',
  otd: 'trend',
  otp: 'trend',
  schedule_adherence: 'trend',
  shipments: 'delivered',
  dwell_time: 'median',
  invoice_disputes: 'count',
  cost: 'total',
  freight_markup: 'total',
  alert_time_to_action: 'median',
};

const initialState: TabState = {
  tabs: { ...defaultTabs },
  filterQueryRedux: null,
  changeType: null,
  changedChartType: null,
};

const tabSlice = createSlice({
  name: 'tabs',
  initialState,
  reducers: {
    setTab(state, action: PayloadAction<{ key: string; value: string }>) {
      state.tabs[action.payload.key] = action.payload.value;
      state.changeType = 'tab';
      state.changedChartType = action.payload.key;
    },
    // Silent merge used to prefill each widget's active lens/metric tab from
    // its persisted layout config on load. Unlike setTab, this doesn't touch
    // changeType/changedChartType — it isn't a user-driven tab change, and
    // flipping those here would race the initial chart-fetch effect into
    // treating hydration as a live tab switch.
    hydrateTabModes(state, action: PayloadAction<Record<string, string>>) {
      state.tabs = { ...state.tabs, ...action.payload };
    },
    setFilterQueryRedux(
      state,
      action: PayloadAction<
        Partial<FilterQuery> & {
          // Explicit null = update stored filters without chart refetch
          // (e.g. All time date reconciliation from button-status).
          changeType?: TabChangeType;
        }
      >
    ) {
      const { changeType: nextChangeType, ...filterFields } = action.payload;
      state.filterQueryRedux = {
        ...state.filterQueryRedux,
        ...filterFields,
      } as FilterQuery;
      // Default omitted changeType to "filter". Explicit null means display-
      // only (no chart invalidation); other values are honored as provided.
      if ('changeType' in action.payload) {
        state.changeType = nextChangeType ?? null;
      } else {
        state.changeType = 'filter';
      }
    },
    setChartChange(
      state,
      action: PayloadAction<{ chartType: string; changeType: 'tab' | 'filter' }>
    ) {
      state.changeType = action.payload.changeType;
      state.changedChartType = action.payload.chartType;
    },
    resetChangeTracking(state) {
      state.changeType = null;
      state.changedChartType = null;
    },

    resetTabs(state) {
      state.tabs = { ...defaultTabs };
      state.filterQueryRedux = null;
      state.changeType = null;
      state.changedChartType = null;
    },
  },
});

export const {
  setTab,
  setFilterQueryRedux,
  setChartChange,
  resetChangeTracking,
  resetTabs,
  hydrateTabModes,
} = tabSlice.actions;
export const tabReducer = tabSlice.reducer;
